const THEME_STORAGE_KEY = "nuvelor_theme";
const THEME_EVENT = "nuvelor-theme-change";

const sharedControlCss = `
  :host {
    --nv-control-size: 40px;
    --nv-control-radius: 13px;
    --nv-control-border: rgba(255,255,255,.16);
    --nv-control-bg: rgba(255,255,255,.06);
    --nv-control-hover: rgba(255,255,255,.10);
    --nv-control-active: rgba(255,255,255,.10);
    --nv-control-accent: #42d9f4;
    --nv-language-accent: #8a4cff;
    --nv-control-text: #cdd6eb;
    --nv-control-active-text: #fff;
    --nv-control-panel: rgba(12,16,28,.96);
    --nv-control-muted: #a2aec7;
    --nv-control-focus: #7be5ff;
    --nv-control-shadow: 0 8px 24px rgba(0,0,0,.50);
    display: inline-block;
    font: inherit;
    color: inherit;
  }

  :host-context(:root[data-nuvelor-theme="light"]) {
    --nv-control-border: #c6d3e3;
    --nv-control-bg: rgba(255,255,255,.76);
    --nv-control-hover: #edf4fb;
    --nv-control-active: #edf4fb;
    --nv-control-accent: #006f8c;
    --nv-language-accent: #6f2bd6;
    --nv-control-text: #4f6078;
    --nv-control-active-text: #202a3b;
    --nv-control-panel: rgba(255,255,255,.96);
    --nv-control-muted: #5b6b83;
    --nv-control-focus: #6f2bd6;
    --nv-control-shadow: 0 8px 24px rgba(31,46,68,.15);
  }

  @media (max-width: 760px) {
    :host {
      --nv-control-size: 38px;
      --nv-control-radius: 12px;
    }
  }
`

function normalizeTheme(value) {
  return value === "light" ? "light" : "dark";
}

function currentTheme() {
  const fromDom = document.documentElement?.dataset?.nuvelorTheme;
  if (fromDom === "light" || fromDom === "dark") return fromDom;
  try {
    return normalizeTheme(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return "dark";
  }
}

function applyTheme(theme, { persist = false, notify = false } = {}) {
  const next = normalizeTheme(theme);
  document.documentElement.dataset.nuvelorTheme = next;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", next === "light" ? "#f3f7fc" : "#0e1623");

  if (persist) {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {}
  }

  if (notify) {
    window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: next }));
  }

  return next;
}

class NuvelorThemeToggle extends HTMLElement {
  static get observedAttributes() {
    return ["label-light", "label-dark"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.onClick = this.onClick.bind(this);
    this.onStorage = this.onStorage.bind(this);
    this.onThemeEvent = this.onThemeEvent.bind(this);
  }

  connectedCallback() {
    if (!this.shadowRoot.innerHTML) {
      this.shadowRoot.innerHTML = `
        <style>
          ${sharedControlCss}
          button {
            width: var(--nv-control-size);
            height: var(--nv-control-size);
            min-width: var(--nv-control-size);
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid var(--nv-control-border);
            border-radius: var(--nv-control-radius);
            background: var(--nv-control-bg);
            color: var(--nv-control-accent);
            box-shadow: var(--nv-control-shadow);
            cursor: pointer;
            transition: background .14s ease, border-color .14s ease, color .14s ease, transform .14s ease;
          }
          button:hover {
            background: var(--nv-control-hover);
            border-color: var(--nv-control-accent);
          }
          button:active {
            background: var(--nv-control-active);
            transform: translateY(1px);
          }
          button:focus-visible {
            outline: 2px solid var(--nv-control-focus);
            outline-offset: 2px;
          }
          svg {
            width: 19px;
            height: 19px;
            fill: none;
            stroke: currentColor;
            stroke-width: 2;
            stroke-linecap: round;
            stroke-linejoin: round;
          }
          .moon { display: none; }
          :host([theme="light"]) .sun { display: none; }
          :host([theme="light"]) .moon { display: block; }
        </style>
        <button type="button">
          <svg class="sun" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="4"></circle>
            <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.66 6.34l1.41-1.41"></path>
          </svg>
          <svg class="moon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20.4 14.2A8.2 8.2 0 0 1 9.8 3.6 8.8 8.8 0 1 0 20.4 14.2Z"></path>
          </svg>
        </button>
      `;
    }

    this.button = this.shadowRoot.querySelector("button");
    this.button?.addEventListener("click", this.onClick);
    window.addEventListener("storage", this.onStorage);
    window.addEventListener(THEME_EVENT, this.onThemeEvent);
    this.sync(currentTheme());
  }

  disconnectedCallback() {
    this.button?.removeEventListener("click", this.onClick);
    window.removeEventListener("storage", this.onStorage);
    window.removeEventListener(THEME_EVENT, this.onThemeEvent);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.sync(currentTheme());
  }

  onClick() {
    const next = applyTheme(currentTheme() === "light" ? "dark" : "light", {
      persist: true,
      notify: true,
    });
    this.sync(next);
  }

  onStorage(event) {
    if (event.key === THEME_STORAGE_KEY) {
      const next = applyTheme(event.newValue, { persist: false, notify: false });
      this.sync(next);
    }
  }

  onThemeEvent(event) {
    this.sync(normalizeTheme(event.detail || currentTheme()));
  }

  sync(theme) {
    const next = normalizeTheme(theme);
    this.setAttribute("theme", next);
    const label = next === "light"
      ? (this.getAttribute("label-dark") || "Use dark theme")
      : (this.getAttribute("label-light") || "Use light theme");
    if (this.button) {
      this.button.setAttribute("aria-label", label);
      this.button.setAttribute("title", label);
    }
  }
}

class NuvelorLanguagePicker extends HTMLElement {
  static get observedAttributes() {
    return ["current", "label", "direction"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.onOptionClick = this.onOptionClick.bind(this);
  }

  connectedCallback() {
    if (!this.shadowRoot.innerHTML) {
      this.shadowRoot.innerHTML = `
        <style>
          ${sharedControlCss}
          details { position: relative; }
          summary {
            list-style: none;
            width: var(--nv-control-size);
            height: var(--nv-control-size);
            min-width: var(--nv-control-size);
            padding: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border: 1px solid var(--nv-control-border);
            border-radius: var(--nv-control-radius);
            background: var(--nv-control-bg);
            color: var(--nv-control-text);
            box-shadow: var(--nv-control-shadow);
            cursor: pointer;
            font: inherit;
            font-size: 12px;
            font-weight: 850;
            letter-spacing: .04em;
            user-select: none;
            transition: background .14s ease, border-color .14s ease, color .14s ease, transform .14s ease;
          }
          summary::-webkit-details-marker { display: none; }
          summary:hover,
          details[open] summary {
            background: var(--nv-control-hover);
            border-color: var(--nv-language-accent);
            color: var(--nv-control-active-text);
          }
          summary:active { transform: translateY(1px); }
          summary:focus-visible {
            outline: 2px solid var(--nv-control-focus);
            outline-offset: 2px;
          }
          .menu {
            display: block;
            position: absolute;
            right: 0;
            min-width: 164px;
            padding: 8px;
            border: 1px solid var(--nv-control-border);
            border-radius: 14px;
            background: var(--nv-control-panel);
            box-shadow: var(--nv-control-shadow);
            backdrop-filter: blur(16px);
            opacity: 0;
            visibility: hidden;
            pointer-events: none;
            transition: opacity .14s ease, transform .14s ease, visibility .14s ease;
            z-index: 50;
          }
          :host(:not([direction="up"])) .menu {
            top: calc(100% + 7px);
            transform: translateY(-4px);
          }
          :host([direction="up"]) .menu {
            bottom: calc(100% + 8px);
            transform: translateY(4px);
          }
          .menu::before {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            height: 8px;
          }
          :host(:not([direction="up"])) .menu::before { top: -8px; }
          :host([direction="up"]) .menu::before { bottom: -8px; }
          details[open] .menu,
          details:focus-within .menu {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
            pointer-events: auto;
          }
          @media (hover: hover) and (pointer: fine) {
            :host(:hover) .menu {
              opacity: 1;
              visibility: visible;
              transform: translateY(0);
              pointer-events: auto;
            }
          }
          ::slotted([data-language-option]) {
            width: 100%;
            min-height: 38px;
            padding: 7px 9px;
            display: flex;
            align-items: center;
            gap: 10px;
            border: 0;
            border-radius: 10px;
            background: transparent;
            color: var(--nv-control-text);
            cursor: pointer;
            font: inherit;
            font-size: 13px;
            text-align: left;
            text-decoration: none;
            box-sizing: border-box;
          }
          ::slotted([data-language-option]:hover),
          ::slotted([data-language-option][aria-current="page"]),
          ::slotted([data-language-option][aria-checked="true"]) {
            background: var(--nv-control-hover);
            color: var(--nv-control-active-text);
          }
          ::slotted([data-language-option]:focus-visible) {
            outline: 2px solid var(--nv-control-focus);
            outline-offset: 1px;
          }
          @media (max-width: 760px) {
            .menu { min-width: 152px; }
          }
        </style>
        <details>
          <summary><span class="current"></span></summary>
          <div class="menu"><slot></slot></div>
        </details>
      `;
    }

    this.details = this.shadowRoot.querySelector("details");
    this.summary = this.shadowRoot.querySelector("summary");
    this.currentNode = this.shadowRoot.querySelector(".current");
    this.addEventListener("click", this.onOptionClick);
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onOptionClick);
  }

  attributeChangedCallback() {
    if (this.isConnected) this.sync();
  }

  onOptionClick(event) {
    if (event.target.closest?.("[data-language-option]")) {
      this.details?.removeAttribute("open");
    }
  }

  sync() {
    const current = this.getAttribute("current") || "EN";
    const label = this.getAttribute("label") || "Language";
    if (this.currentNode) this.currentNode.textContent = current;
    if (this.summary) {
      this.summary.setAttribute("aria-label", label + ": " + current);
      this.summary.setAttribute("title", label);
    }
  }
}

if (typeof customElements !== "undefined") {
  if (!customElements.get("nuvelor-theme-toggle")) {
    customElements.define("nuvelor-theme-toggle", NuvelorThemeToggle);
  }
  if (!customElements.get("nuvelor-language-picker")) {
    customElements.define("nuvelor-language-picker", NuvelorLanguagePicker);
  }
}
